import { Component, type ReactNode } from "react";

/**
 * The last line before a blank page.
 *
 * ⚠ Without a boundary, any throw while rendering — or inside an effect —
 * unmounts the whole tree and leaves an empty white page with nothing to act
 * on. This shows what broke, so a report says more than "it's blank".
 *
 * Deliberately self-contained: no Clerk, no query client, no shared
 * components, because any of those may be what failed.
 */
export class CrashScreen extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown) {
    console.error("Home crashed while rendering", error);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="mx-auto grid min-h-dvh max-w-md place-items-center px-6 py-10">
        <div className="w-full text-center">
          <img
            src="/logo.svg"
            alt=""
            width={48}
            height={48}
            className="mx-auto mb-4 rounded-xl"
          />
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            The page hit an error and could not continue. Reload to try again.
            If it keeps happening, update your browser and report the message
            below to IT.
          </p>
          <p className="text-muted-foreground mt-4 font-mono text-xs break-words">
            {this.state.error.message}
          </p>
          <button
            type="button"
            className="bg-primary text-primary-foreground mt-6 h-10 rounded-md px-4 text-sm hover:opacity-90"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </main>
    );
  }
}
